
import { Component, OnInit, ViewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, Observable, of, switchMap, take, throwError } from 'rxjs';
import { AuthService } from '../../services/auth.service';
import { DomainRequestService } from '../../services/domain-request.service';
import { OrganizationService } from '../../services/organization.service';
import { OrganizationData } from '../../shared/models/organization-data.model';
import { UserRole } from '../../shared/models/user.model';
import { FormAppComponent } from '../form-app/form-app.component';
import { AppFormMode } from '../form-app/models/app-form';
import { FormAppService } from '../form-app/services/form-app.service';

@Component({
    selector: 'app-edit-app',
    imports: [FormAppComponent, MatButtonModule, MatProgressSpinnerModule],
    templateUrl: './edit-app.component.html',
    styleUrl: './edit-app.component.scss',
    providers: [FormAppService, DomainRequestService]
})
export class EditAppComponent implements OnInit {
    public organization: OrganizationData;
    public loading: boolean = true;
    public saving: boolean = false;
    public disabledFields: string[] = ['organizationName'];
    public AppFormMode = AppFormMode;
    public id: string;

    @ViewChild('form') formElement: FormAppComponent;

    constructor(
        private readonly activatedRoute: ActivatedRoute,
        private readonly organizationService: OrganizationService,
        private readonly domainRequestService: DomainRequestService,
        private readonly authService: AuthService,
        private readonly router: Router,
        private readonly formAppService: FormAppService,
        private readonly snackBar: MatSnackBar
    ) {}

    public ngOnInit(): void {
        this.loadOrganization();
    }

    private loadOrganization(): void {
        this.getOrganization().subscribe({
            next: (organization) => {
                this.organization = organization;
                this.loading = false;
                this.id = this.activatedRoute.snapshot.paramMap.get('id')!;

                this.formAppService.setOrganization({
                    ...organization,
                    id: this.id,
                });
            },
            error: () => {
                this.goToDashboard();
            },
        });
    }

    private getOrganization(): Observable<OrganizationData> {
        return this.organizationService.findOrganizationByKey(this.activatedRoute.snapshot.paramMap.get('id')!).pipe(
            take(1),
            switchMap((organization) => {
                if (!organization) {
                    return throwError(() => ({
                        error: 'not-found',
                        message: 'Organization not found',
                    }));
                }

                return combineLatest([
                    this.authService.getAuthenticatedUserRole(),
                    this.authService.getAuthenticatedUserUid(),
                ]).pipe(
                    take(1),
                    switchMap(([userRole, userId]) => {
                        if (userRole !== UserRole.SUPER_ADMIN && organization.userId !== userId) {
                            return throwError(() => ({
                                error: 'permission-denied',
                                message: 'Permission denied',
                            }));
                        }

                        return of(organization);
                    })
                );
            })
        );
    }

    public save(organizationData: OrganizationData): void {
        this.saving = true;

        const domainChanged = this.hasDomainChanged(organizationData);

        this.authService
            .getAuthenticatedUserUid()
            .pipe(
                take(1),
                switchMap((userUid) =>
                    this.organizationService.updateOrganization(this.id, organizationData).pipe(
                        take(1),
                        switchMap(() => {
                            if (!domainChanged) {
                                return of(undefined);
                            }

                            return this.domainRequestService.upsertDomainRequestByOrgId(
                                this.id,
                                {
                                    ...organizationData,
                                    organizationName:
                                        organizationData.organizationName ?? this.organization?.organizationName,
                                },
                                userUid
                            );
                        })
                    )
                )
            )
            .subscribe({
                next: () => {
                    this.snackBar.open('App updated successfully', 'Close', {
                        duration: 3000,
                    });
                    this.goToDashboard();
                },
                error: (err: { message?: string }) => {
                    this.saving = false;
                    this.snackBar.open('Error: ' + (err?.message ?? 'Unknown error'), 'Close', {
                        duration: 3000,
                    });
                },
                complete: () => {
                    this.saving = false;
                },
            });
    }

    public goToDashboard(): void {
        this.router.navigate(['/dashboard']);
    }

    private hasDomainChanged(organizationData: OrganizationData): boolean {
        const currentDomainType = this.organization?.domainType ?? null;
        const nextDomainType = organizationData.domainType ?? null;
        const currentDomainValue = this.normalizeDomain(this.organization?.domainValue);
        const nextDomainValue = this.normalizeDomain(organizationData.domainValue);

        return currentDomainType !== nextDomainType || currentDomainValue !== nextDomainValue;
    }

    private normalizeDomain(domainValue?: string): string {
        return domainValue?.trim().toLowerCase() ?? '';
    }
}
